import {
  AccessDeniedError,
  BadRequestError,
  ConnectionError,
  HttpTypes,
  InternalServerError,
  TooManyRequestsError,
  UnexpectedError,
} from 'src/http';
import { httpResponseHandler } from 'src/http/http-response-handler';
import { fileUploadType } from 'src/types/banners';
import { endpoints } from 'src/utils/axios';
import { Either } from 'src/utils/either';

export class CouponsRepository {
  constructor(private api: HttpTypes.HttpClient) { }

  async getCouponsList(): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.coupons.couponsList,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getLPCouponsList(): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.coupons.lpCouponsList,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCouponDetails(
    id: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.coupons.coupomDetails(id),
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async changeLPCouponStatus(
    id: string,
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/coupons/lp-change-approved-coupon-status`,
      body: { couponId: id },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getLPCouponDetails(
    id: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/coupons/lp-coupon-draft-details/${id}`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getStores(): Promise<
    | Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      undefined
    >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/store/all`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getLocations(): Promise<
    | Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      undefined
    >
    | any
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/store/location/all`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async uploadFile(
    file: fileUploadType
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.files.upload,
      body: { ...file, optimizateImage: true },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createCoupon(
    coupon: any
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.benefits.coupons.createCoupons,
      body: coupon,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async creteLPCoupon(
    coupon: any
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: 'benefits/coupons/lp-create-draft-coupon',
      body: coupon,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async approveLPCoupon(
    couponId: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/coupons/lp-approve-draft-coupon`,
      body: { couponId },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async refuseLPCoupon(
    couponId: string,
    declineComment: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: `benefits/coupons/lp-decline-draft-coupon`,
      body: { couponId, declineComment },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateCoupon(
    coupon: any
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.benefits.coupons.updateCoupon,
      body: coupon,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async updateLPCoupon(
    coupon: any
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: 'benefits/coupons/lp-update-draft-coupon',
      body: coupon,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async exportCoupon(
    ids: string[],
    userName: string,
    options?: { isAllSelected?: boolean }
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.benefits.coupons.exportData,
      body: {
        ids,
        userName,
        isAllSelected: Boolean(options?.isAllSelected),
      },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getAllParkings(): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.coupons.parkingsList,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getCouponsByApprovalStatus(
    params: Object
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: 'benefits/coupons/lp-coupons-by-approval-status',
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }
}
